/**
 * Auth API Hooks (TanStack Query)
 *
 * TanStack Query hooks for authentication state management.
 * Provides query options for fetching current user and logout mutation.
 *
 * Features:
 * - Fetches current user from /api/v1/auth/me (JWT cookie-based)
 * - Handles 401 gracefully (returns null for unauthenticated)
 * - 5-minute stale time for auth state caching
 * - Refetches on window focus for session changes
 * - Logout mutation clears cache and handles redirect
 *
 * @layer features
 * @slice auth
 * @segment api
 * @example
 * // Use in component
 * const { data: user, isLoading } = useQuery(authMeQueryOptions());
 *
 * @example
 * // Use logout
 * const logout = useLogoutMutation();
 * await logout.mutateAsync();
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-keys
 * @added DOC-0073 (2025-12-19)
 * @updated DOC-0094 (2025-12-26) - Migrated to FSD features/auth
 */

import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@shared/api';
import { setRequesterExportProfile } from '@shared/lib/requester-export-references';
import { z } from 'zod';
import { clearQueryResponseCache } from '@shared/lib/query-helpers';
import type { User } from '@entities/user';
import type { AuthMeResponse, SessionData } from '@entities/session';

type AuthMeApiResponse = AuthMeResponse | User;

export interface RegisterRequest {
  email: string;
  locale: string;
  idempotencyKey: string;
}

const registerResponseSchema = z.object({
  data: z.object({
    nextAction: z.literal('enter_code'),
    expiresAt: z.iso.datetime(),
    resendAvailableAt: z.iso.datetime(),
  }),
});

export type RegisterResponse = z.infer<typeof registerResponseSchema>;

export type RegistrationResult = RegisterResponse;

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: true;
  user: User;
}

export interface AccountLoginSettings {
  email: string;
  passwordConfigured: boolean;
  sso: {
    enabled: boolean;
    provider: 'move.molchanovs.com' | null;
    providerLabel: string | null;
  };
}

export interface AccountLoginSettingsResponse {
  account: AccountLoginSettings;
}

export interface AccountPasswordSetupRequest {
  password: string;
  passwordConfirm: string;
}

export interface AccountPasswordSetupResponse {
  success: true;
  passwordConfigured: true;
}

function normalizeAuthMeResponse(response: AuthMeApiResponse): SessionData {
  if ('user' in response) {
    return {
      user: response.user,
      permissions: response.permissions ?? null,
    };
  }

  return {
    user: response,
    permissions: null,
  };
}

/**
 * Query key factory for auth-related queries
 *
 * Follows TanStack Query key factory pattern for
 * type-safe hierarchical cache invalidation.
 */
export const authKeys = {
  /** Base key for all auth queries */
  all: ['auth'] as const,
  /** Key for current user query */
  me: () => [...authKeys.all, 'me'] as const,
  /** Key for account login settings */
  account: () => [...authKeys.all, 'account'] as const,
} as const;

function clearUserScopedQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({
    // Preserve the observer behind AuthProvider, but discard every other cached value
    // that belongs to the previous authenticated user.
    predicate: (query) =>
      query.queryKey[0] !== authKeys.all[0] || query.queryKey[1] !== authKeys.me()[1],
  });
}

/** Refresh backend permissions after a committed role save without retaining stale grants. */
export async function refreshPermissionsAfterRoleSave(
  queryClient: QueryClient,
  targetUserId: string | null
): Promise<void> {
  const session = queryClient.getQueryData<SessionData | null>(authKeys.me());
  const selfChanged = session?.user?.id === targetUserId;
  await queryClient.cancelQueries({ queryKey: authKeys.me(), exact: true });
  if (selfChanged) {
    // Cancel responses authorized before Save and clear protected data even when refetch denies.
    await queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
    clearQueryResponseCache();
    clearUserScopedQueries(queryClient);
  }

  // A refresh failure is exposed through AuthProvider.error; the committed save remains successful.
  await queryClient.invalidateQueries({ queryKey: authKeys.me(), exact: true });
}

/**
 * Fetch current user from JWT cookie
 *
 * Makes a GET request to /api/v1/auth/me endpoint.
 * The backend validates the JWT from HttpOnly cookie
 * and returns user data.
 *
 * @returns User object if authenticated, null if not
 * @throws ApiError for non-401 errors
 */
export async function fetchCurrentUser(): Promise<SessionData | null> {
  try {
    const response = await api.get<AuthMeApiResponse>('/api/v1/auth/me', {
      // Include cookies for JWT authentication
      headers: {},
    });
    return normalizeAuthMeResponse(response);
  } catch (error) {
    // 401 = not authenticated, return null (expected case)
    if (error instanceof ApiError && error.status === 401) {
      return null;
    }
    // Re-throw other errors (500, network, etc.)
    throw error;
  }
}

async function fetchPostAuthSession(user: User): Promise<SessionData> {
  const session = await fetchCurrentUser();
  return (
    session ?? {
      user,
      permissions: null,
    }
  );
}

/**
 * Query options for fetching current user
 *
 * Use with useQuery or useSuspenseQuery:
 * @example
 * const { data: user, isLoading } = useQuery(authMeQueryOptions());
 */
export function authMeQueryOptions() {
  return queryOptions({
    queryKey: authKeys.me(),
    queryFn: fetchCurrentUser,
    // Cache auth state for 5 minutes before refetching
    staleTime: 5 * 60 * 1000,
    // Keep in garbage collection for 10 minutes after unmount
    gcTime: 10 * 60 * 1000,
    // Don't retry auth failures (401 is expected for logged-out users)
    retry: false,
    // Refetch when user returns to tab (detect session changes)
    refetchOnWindowFocus: true,
    // Don't refetch on mount if data is fresh
    refetchOnMount: true,
  });
}

async function fetchAccountLoginSettings(): Promise<AccountLoginSettingsResponse> {
  return await api.get<AccountLoginSettingsResponse>('/api/v1/auth/account');
}

export function accountLoginSettingsQueryOptions() {
  return queryOptions({
    queryKey: authKeys.account(),
    queryFn: fetchAccountLoginSettings,
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useAccountPasswordSetupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AccountPasswordSetupRequest) => {
      return await api.post<AccountPasswordSetupResponse, AccountPasswordSetupRequest>(
        '/api/v1/auth/account/password',
        payload
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authKeys.account() });
    },
  });
}

/**
 * Logout mutation hook
 *
 * Calls POST /api/v1/auth/logout to clear server session,
 * then clears client-side cache and redirects to login page.
 *
 * @example
 * const logout = useLogoutMutation();
 *
 * const handleLogout = () => {
 *   logout.mutate();
 * };
 *
 * // Or with await
 * await logout.mutateAsync();
 */
export function useLogoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Call backend logout endpoint to clear server session
      await api.post('/api/v1/auth/logout', undefined);
    },
    onSuccess: () => {
      setRequesterExportProfile(null);
      clearQueryResponseCache();
      queryClient.clear();
      // Clear auth cache immediately
      queryClient.setQueryData(authKeys.me(), null);
      // Invalidate all auth-related queries
      void queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
    onError: (error) => {
      // Log error but still clear local state
      console.error('Logout failed:', error);

      // Clear local state anyway (user wants to log out)
      setRequesterExportProfile(null);
      clearQueryResponseCache();
      queryClient.clear();
      queryClient.setQueryData(authKeys.me(), null);
      void queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
}

/** Begin email activation without creating an authenticated session. */
export function useRegisterMutation() {
  return useMutation({
    mutationFn: async ({ signal, ...payload }: RegisterRequest & { signal?: AbortSignal }) =>
      await api.post('/api/v1/auth/register', payload, { schema: registerResponseSchema, signal }),
    retry: false,
  });
}

/**
 * Login mutation hook
 *
 * Calls POST /api/v1/auth/login to authenticate a user
 * and seeds auth cache with the returned user.
 */
export function useLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: LoginRequest) => {
      return await api.post<LoginResponse, LoginRequest>('/api/v1/auth/login', payload);
    },
    onSuccess: async (response) => {
      clearQueryResponseCache();
      clearUserScopedQueries(queryClient);
      queryClient.setQueryData(authKeys.me(), await fetchPostAuthSession(response.user));
      void queryClient.invalidateQueries({ queryKey: authKeys.account() });
    },
  });
}
